import React from 'react';

import { Card, FormItem, Toggle } from '../../../components';
import { Input } from '../../../components/Input';
import type { AgentForm as AgentFormType } from '../../../utilities';
import { validateMinValue } from '../utils/validators';

export type TtsCacheSettingsProps = {
  disabled?: boolean;
  form: AgentFormType;
};

export const TtsCacheSettings: React.FC<TtsCacheSettingsProps> = ({
  disabled,
  form,
}) => {
  return (
    <Card
      collapsable
      size="unset"
      title="TTS Cache"
      name="agentpage.ttsCacheSettings"
      contentClassName="flex flex-col gap-y-md"
      subtitle="Configure Redis-backed TTS response caching for this agent"
    >
      <FormItem<AgentFormType>
        name="tts_cache_settings_enabled"
        label="Enable TTS cache"
        tooltip="Master switch for this agent"
        className="flex justify-between items-center"
      >
        <Toggle disabled={disabled} />
      </FormItem>
      {form.tts_cache_settings_enabled && (
        <>
          <FormItem<AgentFormType>
            name="tts_cache_settings_ttl_seconds"
            label="TTL (seconds)"
            tooltip="Redis TTL per entry. Refreshed on every store, not on read. Default: 604800 (7 days)."
            validate={validateMinValue(0)}
          >
            <Input numeric min={0} roundTo={0} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="tts_cache_settings_lookup_timeout_ms"
            label="Lookup timeout (ms)"
            tooltip="Hard ceiling on a cache read. Exceeded → treated as a miss, the provider runs as usual. Measure your Redis first before tuning."
            validate={validateMinValue(0)}
          >
            <Input numeric min={0} roundTo={0} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="tts_cache_settings_max_text_chars"
            label="Max text chars"
            tooltip="Longer text is neither looked up nor stored (long one-off replies would only churn Redis)."
            validate={validateMinValue(0)}
          >
            <Input numeric min={0} roundTo={0} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="tts_cache_settings_max_audio_bytes"
            label="Max audio bytes"
            tooltip="Audio above this is not stored. Default: 2097152 (~2 MiB, ~43 s at 24 kHz mono)."
            validate={validateMinValue(0)}
          >
            <Input numeric min={0} roundTo={0} disabled={disabled} />
          </FormItem>
          <FormItem<AgentFormType>
            name="tts_cache_settings_stream_lookup_grace_ms"
            label="Stream lookup grace (ms)"
            tooltip="Streaming providers only: max time to hold the first text chunk waiting for the segment to complete before giving up on a lookup."
            validate={validateMinValue(0)}
          >
            <Input numeric min={0} roundTo={0} disabled={disabled} />
          </FormItem>
        </>
      )}
    </Card>
  );
};
